import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { ServiceLayout } from "../components/ServiceLayout";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { PHOTOS } from "../data/site";

const PICKS = [
    {
        name: "Gutter Cleaning",
        desc: "Leaves, debris and build-up cleared from your gutters so water flows the way it should.",
        service: "Gutter Cleaning",
        img: PHOTOS.gutterHand,
        testid: "pick-gutter-cleaning",
    },
    {
        name: "Window Cleaning",
        desc: "Windows washed and left clear and streak-free, with frames and sills wiped down.",
        service: "Window Cleaning",
        img: PHOTOS.window,
        testid: "pick-window-cleaning",
    },
];

export default function GutterWindow() {
    return (
        <div data-testid="gutter-window-page">
            <Seo title="Gutter & Window Cleaning Adelaide | Lawn Busters"
                description="Gutter vacuum cleaning and streak-free window cleaning for Adelaide homes. Book one service or bundle both in one visit. 5.0-star rated. Free quotes." />
            <ServiceLayout
                eyebrow="Gutter & Window Cleaning — Adelaide"
                titleLines={["Gutter & Window", "Cleaning"]}
                intro="Blocked gutters and grimy windows drag a whole property down. We clean both, safely and thoroughly, for homes and properties across Adelaide."
                image={PHOTOS.gutter}
                imageAlt="Gutter cleaning on an Adelaide home"
                preselect="Gutter Cleaning"
                stockImage={false}
                stockSecond={false}
                items={[
                    { name: "Gutter cleaning", desc: "Leaves, sticks and built-up debris cleared from your gutters so water flows freely again." },
                    { name: "Window cleaning", desc: "Windows washed and left clear and streak-free." },
                    { name: "Frames, tracks and sills", desc: "Dirt and cobwebs wiped from window frames, tracks and sills." },
                    { name: "Debris and leaf removal", desc: "Roof valleys and gutter lines cleared of built-up leaves and debris." },
                    { name: "Gutter flow check while we clean", desc: "Once cleared, we check that water flows the way it should." },
                    { name: "Roof-line tidy up", desc: "Any mess around the roof line is cleaned up before we leave." },
                ]}
            >
                <section className="mx-auto max-w-6xl px-6 py-16 lg:py-24" data-testid="pick-services-section">
                    <Reveal>
                        <p className="eyebrow text-forest-leaf">Your choice</p>
                        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                            Choose one — or book both together
                        </h2>
                        <p className="mt-4 max-w-xl text-base text-moss">
                            Need just your gutters done? Only the windows? Or both in the same visit? Simply
                            select what you need in the booking form — you can combine services in a single booking.
                        </p>
                    </Reveal>
                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {PICKS.map((p, i) => (
                            <Reveal key={p.name} delay={i * 0.1}>
                                <div data-testid={p.testid} className="overflow-hidden rounded-3xl border border-forest/10 bg-bone-card">
                                    <div className="relative">
                                        <img src={p.img} alt={p.name} className="aspect-[16/9] w-full object-cover" />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="flex items-center gap-2 font-display text-xl font-semibold text-ink">
                                            <Check size={16} className="text-forest-leaf" /> {p.name}
                                        </h3>
                                        <p className="mt-2 text-sm text-moss">{p.desc}</p>
                                        <Link to={`/book-now?services=${encodeURIComponent(p.service)}`} data-testid={`${p.testid}-book`}
                                            className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-forest hover:text-forest-leaf">
                                            Book {p.name} <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <Link to="/book-now?services=Gutter%20Cleaning%2CWindow%20Cleaning" data-testid="book-both-button"
                            className="mt-8 inline-block rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                            Book Gutter + Window Together <ArrowRight size={14} className="ml-1 inline" />
                        </Link>
                    </Reveal>
                </section>
            </ServiceLayout>
        </div>
    );
}
